import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Base64, fetchData, postData, SmartImageComponent, malequestions } from '../components/misc';
import { Loader } from '../components/profile';
import { MenDashboard } from '../components/signup';

export default function ActivationProfile() {
    const { email } = useParams();
    const [profile, setProfile] = useState(null);
    const [chats, setChats] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [men, setMen] = useState([]);
    const [matches, setMatches] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [bridgeActions, setBridgeActions] = useState([]);
    const [postSignupBridgeActions, setPostSignupBridgeActions] = useState([]);
    const [profileViewHistory, setProfileViewHistory] = useState([]);
    const [bridgeVisitorIds, setBridgeVisitorIds] = useState([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [loadingChats, setLoadingChats] = useState(true);
    const [loadingTasks, setLoadingTasks] = useState(true);
    const [loadingMen, setLoadingMen] = useState(true);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [loadingBridgeActions, setLoadingBridgeActions] = useState(true);
    const [loadingProfileViewHistory, setLoadingProfileViewHistory] = useState(true);
    const [showMatchesSection, setShowMatchesSection] = useState(true);
    const [showProfile, setShowProfile] = useState(false);
    const [showOtherProfiles, setShowOtherProfiles] = useState(false);
    const [showBridgeActions, setShowBridgeActions] = useState(false);
    const [showProfileViewHistory, setShowProfileViewHistory] = useState(true);

    const [oldMaleName, setOldMaleName] = useState('');
    const [newMaleName, setNewMaleName] = useState('');
    const [renameStatus, setRenameStatus] = useState('');

    async function renameChatFiles() {
        if (!oldMaleName.trim() || !newMaleName.trim()) {
            setRenameStatus('Please provide both old and new male names.');
            return;
        }

        const formData = new FormData();
        formData.append('email', email);
        formData.append('old_male_name', oldMaleName.trim());
        formData.append('new_male_name', newMaleName.trim());

        try {
            const result = await postData('/api/sirri_api/chats/rename_files', formData);
            if (result.responsecode === 200) {
                setRenameStatus(`Renamed ${result.count || 0} chat file(s).`);
                fetchData(`/api/chats/responder/${email}`).then((data) => {
                    if (data.responsecode === 200) {
                        setChats(data.chats_data || {});
                    }
                });
            } else {
                setRenameStatus(result.message || 'Unable to rename chat files.');
            }
        } catch {
            setRenameStatus('Unable to rename chat files.');
        }
    }

    useEffect(() => {
        const encoded = Base64.encode(email);
        fetchData(`/api/getprofile/${encoded}`).then((data) => {
            if (data.responsecode === 200) {
                setProfile(data.data);
            }
            setLoadingProfile(false);
        }).catch(() => setLoadingProfile(false));

        fetchData(`/api/chats/responder/${email}`).then((data) => {
            if (data.responsecode === 200) {
                setChats(data.chats_data || {});
            }
            setLoadingChats(false);
        }).catch(() => setLoadingChats(false));

        fetchData(`/api/sirri_api/tasks/${email}`).then((data) => {
            if (data.responsecode === 200) {
                setTasks(data.tasks || []);
            }
            setLoadingTasks(false);
        }).catch(() => setLoadingTasks(false));

        fetchData('/api/get_men').then((data) => {
            if (data.responsecode === 200) {
                setMen(data.acc || []);
            }
            setLoadingMen(false);
        }).catch(() => setLoadingMen(false));

        fetchData('/api/support/list_chats').then((data) => {
            if (data.chats) {
                const filtered = data.chats.filter(t => t.email === email);
                setTickets(filtered);
            }
            setLoadingTickets(false);
        }).catch(() => setLoadingTickets(false));

        fetchData(`/api/sirri_api/bridge_actions/${encodeURIComponent(email)}`).then((data) => {
            if (data.responsecode === 200) {
                setBridgeActions(data.pre_signup_actions || data.actions || []);
                setPostSignupBridgeActions(data.post_signup_actions || []);
                setBridgeVisitorIds(data.visitor_ids || []);
            }
            setLoadingBridgeActions(false);
        }).catch(() => setLoadingBridgeActions(false));

        fetchData(`/api/sirri_api/profile_view_history/${encodeURIComponent(email)}?limit=25`).then((data) => {
            if (data.responsecode === 200) {
                setProfileViewHistory(data.views || []);
            }
            setLoadingProfileViewHistory(false);
        }).catch(() => setLoadingProfileViewHistory(false));
    }, [email]);

    useEffect(() => {
        if (!profile || men.length === 0) return;

        function isMatch(item) {
            if (item.gender1 !== 'Male') return false;
            if (!item.age1) return false;
            if (item.EMAIL.includes('@gmail.com') || item.EMAIL.includes('@yahoo.com') || item.EMAIL.includes('@outlook.com')) {
                return false;
            }

            const min = profile.minage1 || '';
            const max = profile.maxage1 || '';
            if (min !== '' && Number(item.age1) < Number(min)) return false;
            if (max !== '' && Number(item.age1) > Number(max)) return false;

            const profCountry = (profile.country1 || '').trim().toLowerCase();
            if (profCountry !== '' && (item.country1 || '').trim().toLowerCase() !== profCountry) {
                return false;
            }
            return true;
        }

        const filtered = men.filter(isMatch);
        setMatches(filtered);
    }, [men, profile]);

    const history = React.useMemo(() => {
        if (!profile) return [];
        try {
            return JSON.parse(profile.package_history || '[]');
        } catch {
            return [];
        }
    }, [profile]);

    return (
        <div className="flex flex-col w-full items-center">
            {loadingProfile ? <Loader /> : (
                profile && (
                    <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                        <div className="flex items-center mb-4">
                            <SmartImageComponent cssclasses="w-24 h-24 rounded-full mr-4" src={`/api/profilepic/1?EMAIL=${email}`} id="Profile" backup={true}/>
                            <div>
                                <p className="text-lg">
                                    {profile.fullname1}
                                    {profile.lastlogin && (
                                        <span className="text-xs ml-2">Last login: {new Date(profile.lastlogin * 1000).toLocaleString()}</span>
                                    )}
                                </p>
                                <p className="text-sm">{profile.phone1}</p>
                            </div>
                            <button className="ml-auto bg-white text-black px-4 py-2 rounded" onClick={() => setShowProfile(!showProfile)}>{showProfile ? 'Hide' : 'Show'} details</button>
                        </div>
                        {showProfile && Object.keys(profile).filter(k => !['package_history','__id'].includes(k)).map((k, idx) => (
                            <p key={idx} className="text-sm break-words"><strong>{k}:</strong> {String(profile[k])}</p>
                        ))}
                    </div>
                )
            )}

            {loadingProfile ? null : (
                <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                    <h2 className="text-xl mb-2">Package History</h2>
                    {history.length === 0 ? (
                        <p className="text-sm">No history.</p>
                    ) : (
                        <ul className="text-sm list-disc list-inside">
                            {history.map((h, idx) => (
                                <li key={idx}>{new Date(h.timestamp * 1000).toLocaleString()} - {h.package}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {loadingTasks ? <Loader /> : (
                <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                    <h2 className="text-xl mb-2">Tasks</h2>
                    {tasks.length > 0 ? (
                        <ul className="text-sm list-disc list-inside">
                            {tasks.map((t, idx) => (
                                <li key={idx}>{t.man.fullname1} - {t.message} ({t.time_str})</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm">No tasks.</p>
                    )}
                </div>
            )}

            {loadingTickets ? <Loader /> : (
                <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                    <h2 className="text-xl mb-2">Support Tickets</h2>
                    {tickets.length > 0 ? (
                        <ul className="text-sm list-disc list-inside">
                            {tickets.map((t, idx) => (
                                <li key={idx}>Ticket: {t.ticket} - {t.resolved ? 'Resolved' : 'Open'}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm">No tickets.</p>
                    )}
                </div>
            )}

            {loadingProfileViewHistory ? <Loader /> : (
                <ProfileViewHistoryPanel
                    views={profileViewHistory}
                    show={showProfileViewHistory}
                    setShow={setShowProfileViewHistory}
                />
            )}

            {loadingBridgeActions ? <Loader /> : (
                <BridgeActionsPanel
                    preSignupActions={bridgeActions}
                    postSignupActions={postSignupBridgeActions}
                    visitorIds={bridgeVisitorIds}
                    show={showBridgeActions}
                    setShow={setShowBridgeActions}
                />
            )}

            {loadingMen ? <Loader /> : (
                <>
                <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-xl">Matches</h2>
                        <button className="bg-white text-black px-2 py-1 rounded text-xs" onClick={() => setShowMatchesSection(!showMatchesSection)}>{showMatchesSection ? 'Hide' : 'Show'}</button>
                    </div>
                    {showMatchesSection && (
                        matches.length > 0 ? (
                            <ul className="text-sm list-disc list-inside">
                                {matches.map((m, idx) => (
                                    <li key={idx} className="flex justify-between items-center">
                                        <span>{m.fullname1} - {m.ethnicity1} - {m.age1}</span>
                                        <button className="bg-white text-black px-2 py-1 rounded text-xs" onClick={() => navigator.clipboard.writeText(`/profile/${Base64.encode(m.fullname1)}?id=${profile?.__id}&EMAIL=${Base64.encode(email)}`)}>Copy link</button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm">No matches.</p>
                        )
                    )}
                </div>

                <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-xl">Other Profiles</h2>
                        <button
                            className="bg-white text-black px-2 py-1 rounded text-xs"
                            onClick={() => setShowOtherProfiles(!showOtherProfiles)}
                        >
                            {showOtherProfiles ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    {showOtherProfiles && <MenDashboard questions={malequestions} />}
                </div>
                </>
            )}


            <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
                <h2 className="text-xl mb-2">Rename Chat Files</h2>
                <p className="text-sm mb-2">Rename chat files for this profile when a male profile name changes.</p>
                <div className="flex flex-col gap-2">
                    <input
                        className="text-black p-2 rounded"
                        placeholder="Old male name"
                        value={oldMaleName}
                        onChange={(e) => setOldMaleName(e.target.value)}
                    />
                    <input
                        className="text-black p-2 rounded"
                        placeholder="New male name"
                        value={newMaleName}
                        onChange={(e) => setNewMaleName(e.target.value)}
                    />
                    <button className="bg-white text-black px-3 py-2 rounded w-fit" onClick={renameChatFiles}>Rename chat files</button>
                    {renameStatus ? <p className="text-xs">{renameStatus}</p> : null}
                </div>
            </div>

            {loadingChats ? <Loader /> : (
                <div className="bg-black text-white p-4 rounded w-full max-w-3xl">
                    <h2 className="text-xl mb-2">Chats</h2>
                    {chats && Object.keys(chats).length > 0 ? (
                        Object.keys(chats).map((name, idx) => (
                            <div key={idx} className="border border-white p-2 mb-2 rounded">
                                <p className="mb-1">{name}</p>
                                {chats[name].map((chat, cIdx) => (
                                    <p key={cIdx} className="text-sm">{chat.female} - {chat.male} ({chat.all_num_msg} messages)</p>
                                ))}
                            </div>
                        ))
                    ) : (
                        <p className="text-sm">No chats.</p>
                    )}
                </div>
            )}
        </div>
    );
}


function formatBridgeViewTime(epoch, fallback) {
    if (epoch) {
        return new Date(epoch * 1000).toLocaleString();
    }
    return fallback || 'Unknown time';
}

function formatDuration(seconds, milliseconds) {
    const numericSeconds = Number(seconds || 0);
    const fallbackSeconds = Number(milliseconds || 0) / 1000;
    const totalSeconds = numericSeconds > 0 ? numericSeconds : fallbackSeconds;
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
        return 'Unknown duration';
    }
    const rounded = Math.round(totalSeconds);
    const minutes = Math.floor(rounded / 60);
    const secs = rounded % 60;
    if (minutes <= 0) {
        return `${secs}s`;
    }
    return `${minutes}m ${secs}s`;
}

function describeViewEvent(eventName) {
    if (eventName === 'chat_view_duration') return 'Chat';
    if (eventName === 'admin_chat_view_duration') return 'Admin chat';
    if (eventName === 'match_profile_view_duration') return 'Match profile';
    return 'Profile';
}

function ProfileViewHistoryPanel({ views, show, setShow }) {
    const visibleViews = useMemo(() => views || [], [views]);
    return (
        <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
            <div className="flex justify-between items-center mb-2">
                <div>
                    <h2 className="text-xl">Last People Viewed</h2>
                    <p className="text-xs text-gray-300">Recent profile and chat views by this account.</p>
                </div>
                <button className="bg-white text-black px-2 py-1 rounded text-xs" onClick={() => setShow(!show)}>
                    {show ? 'Hide' : 'Show'}
                </button>
            </div>
            <p className="text-sm mb-2">{visibleViews.length} view record(s) found.</p>
            {show && (
                visibleViews.length > 0 ? (
                    <div className="flex flex-col gap-2">
                        {visibleViews.map((view, idx) => (
                            <div key={`${view.viewed_email}-${view.server_received_epoch}-${idx}`} className="border border-gray-700 rounded p-2 text-sm">
                                <p className="font-bold break-words">{view.viewed_email || 'Unknown email'}</p>
                                {view.viewed_name ? <p className="text-xs text-gray-300">{view.viewed_name}</p> : null}
                                <p className="text-xs"><strong>Type:</strong> {describeViewEvent(view.event_name)}</p>
                                <p className="text-xs"><strong>Viewed for:</strong> {formatDuration(view.duration_seconds, view.duration_ms)}</p>
                                <p className="text-xs"><strong>When:</strong> {formatBridgeViewTime(view.server_received_epoch, view.server_received_at || view.client_event_time)}</p>
                                {view.path ? <p className="text-xs break-words"><strong>Path:</strong> {view.path}</p> : null}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm">No profile or chat view durations found for this account yet.</p>
                )
            )}
        </div>
    );
}



function BridgeActionList({ actions, emptyText }) {
    return (
        actions.length > 0 ? (
            <div className="flex flex-col gap-2">
                {actions.map((action, idx) => {
                    const title = (action.event_names || []).join(', ') || 'bridge_action';
                    const time = action.server_received_epoch
                        ? new Date(action.server_received_epoch * 1000).toLocaleString()
                        : (action.server_received_at || action.client_event_time || 'Unknown time');
                    return (
                        <details key={idx} className="border border-gray-700 rounded p-2 text-sm">
                            <summary className="cursor-pointer">
                                <span className="font-bold">{title}</span>
                                <span className="text-gray-300"> — {time}</span>
                            </summary>
                            <div className="mt-2 text-xs break-words">
                                <p><strong>Path:</strong> {action.path || 'Unknown'}</p>
                                <p><strong>Host:</strong> {action.host || 'Unknown'}</p>
                                <p><strong>Visitor:</strong> {action.visitor_id || 'Unknown'}</p>
                                <p><strong>Session:</strong> {action.session_id || 'Unknown'}</p>
                                <p><strong>Sequence:</strong> {action.event_seq || 'Unknown'}</p>
                                <p><strong>Matched by:</strong> {(action.matched_by || []).join(', ') || 'Unknown'}</p>
                                <pre className="bg-gray-900 p-2 rounded mt-2 whitespace-pre-wrap overflow-x-auto">{JSON.stringify(action.details || {}, null, 2)}</pre>
                            </div>
                        </details>
                    );
                })}
            </div>
        ) : (
            <p className="text-sm">{emptyText}</p>
        )
    );
}

function BridgeActionsPanel({ preSignupActions, postSignupActions, visitorIds, show, setShow }) {
    const preActions = preSignupActions || [];
    const postActions = postSignupActions || [];
    return (
        <div className="bg-black text-white p-4 rounded w-full max-w-3xl mb-4">
            <div className="flex justify-between items-center mb-2">
                <div>
                    <h2 className="text-xl">Bridge Actions</h2>
                    <p className="text-xs text-gray-300">
                        Visitor IDs: {visitorIds.length > 0 ? visitorIds.join(', ') : 'None found'}
                    </p>
                </div>
                <button className="bg-white text-black px-2 py-1 rounded text-xs" onClick={() => setShow(!show)}>
                    {show ? 'Hide' : 'Show'}
                </button>
            </div>
            <p className="text-sm mb-2">
                {preActions.length} pre-signup action(s), {postActions.length} post-signup action(s) found.
            </p>
            {show && (
                <div className="flex flex-col gap-4">
                    <section>
                        <h3 className="text-lg mb-2">Pre-signup Actions</h3>
                        <BridgeActionList actions={preActions} emptyText="No pre-signup bridge actions found for this profile." />
                    </section>
                    <section>
                        <h3 className="text-lg mb-2">Post-signup Actions</h3>
                        <BridgeActionList actions={postActions} emptyText="No post-signup bridge actions found for this profile." />
                    </section>
                </div>
            )}
        </div>
    );
}
